<template>
  <div
    :class="[
      'badiu-tiptap-editor',
      fullscreen ? 'badiu-tiptap-fullscreen' : ''
    ]"
    :style="editorStyle"
  >
    <!-- Isolated toolbar -->
    <badiu-tiptap-toolbar
      :editor="editor"
      :fullscreen="fullscreen"
      @open-link="openLinkModal"
      @open-image="openImageModal"
      @open-source="openSourceModal"
      @insert-table="insertTable"
      @toggle-fullscreen="toggleFullscreen"
    />

    <!-- Editable content area -->
    <editor-content
      :editor="editor"
      class="badiu-tiptap-content"
    />

    <!-- Status bar (footer) — TinyMCE-style -->
    <div
      v-if="editor"
      class="badiu-tiptap-statusbar"
    >
      <span
        v-if="showCounter && editor.storage.characterCount"
        class="badiu-tiptap-counter"
      >
        {{ editor.storage.characterCount.characters() }} characters
        ·
        {{ editor.storage.characterCount.words() }} words
      </span>

      <span class="badiu-tiptap-brand">
        Badiu Editor
      </span>
    </div>

    <!-- Image modal (isolated component) -->
    <badiu-tiptap-image
      :show="showImageModal"
      :initial="imageInitial"
      :config="config"
      @save="saveImage"
      @cancel="closeImageModal"
      @uploaded="onImageUploaded"
      @error="onUploadError"
    />

    <!-- Link modal (isolated component) -->
    <badiu-tiptap-link
      :show="showLinkModal"
      :initial="linkInitial"
      :config="config"
      @save="saveLink"
      @remove="removeLink"
      @cancel="closeLinkModal"
      @uploaded="onLinkUploaded"
      @error="onUploadError"
    />

    <!-- Source code modal (isolated component) -->
    <badiu-tiptap-source-code
      :show="showSourceModal"
      :html="sourceHtml"
      @save="saveSource"
      @cancel="closeSourceModal"
    />
  </div>
</template>

<script>
/**
 * BadiuTiptapEditor
 *
 * TinyMCE-inspired HTML editor built on Tiptap:
 *  - Toolbar grouped into logical sections (BadiuTiptapToolbar)
 *  - Separate modal for each feature:
 *     · BadiuTiptapImage       → image modal (form + preview)
 *     · BadiuTiptapImageUpload → isolated image upload
 *     · BadiuTiptapFileUpload  → generic file upload (used by link)
 *     · BadiuTiptapLink        → link modal (URL / attachment / target)
 *     · BadiuTiptapSourceCode  → HTML source view / edit modal
 *  - Fullscreen mode
 *  - Paste images as base64
 */
import { ref, reactive, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { Editor, EditorContent } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import Image from '@tiptap/extension-image'
import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'
import TextAlign from '@tiptap/extension-text-align'
import Underline from '@tiptap/extension-underline'
import Table from '@tiptap/extension-table'
import TableRow from '@tiptap/extension-table-row'
import TableCell from '@tiptap/extension-table-cell'
import TableHeader from '@tiptap/extension-table-header'
import TaskList from '@tiptap/extension-task-list'
import TaskItem from '@tiptap/extension-task-item'
import CharacterCount from '@tiptap/extension-character-count'

import {
  applyExternalHtml,
  commitSourceHtml,
  createHtmlFidelityState,
  toCssSize
} from '../../editor-state.js'
import {
  createPreservedHtmlExtension,
  createPreservedHtmlRegistry
} from '../../preserved-html.js'

import BadiuTiptapToolbar from './BadiuTiptapToolbar.vue'
import BadiuTiptapImage from './BadiuTiptapImage.vue'
import BadiuTiptapLink from './BadiuTiptapLink.vue'
import BadiuTiptapSourceCode from './BadiuTiptapSourceCode.vue'

// Full whitespace preservation is unsafe inside schema-based tables: the
// indentation between cells is interpreted as content and repaired into extra
// columns/rows. Exact formatting lives in the source snapshot/registry below.
const HTML_PARSE_OPTIONS = Object.freeze({});

// Attributes that already belong to a Tiptap extension must stay under that
// extension's control. Everything else (including data-*, aria-* and legacy
// table attributes such as border/cellpadding) can safely round-trip through a
// single map stored in the ProseMirror node/mark.
const MANAGED_ATTRIBUTES_BY_TAG = Object.freeze({
  a: new Set(["href", "target", "rel", "title"]),
  img: new Set(["src", "alt", "title", "width", "height"]),
  ol: new Set(["start", "type"]),
  li: new Set(["data-type", "data-checked", "checked"]),
  td: new Set(["colspan", "rowspan", "data-colwidth"]),
  th: new Set(["colspan", "rowspan", "data-colwidth"])
});

const MANAGED_STYLES_BY_TAG = Object.freeze({
  p: ["text-align"],
  h1: ["text-align"],
  h2: ["text-align"],
  h3: ["text-align"],
  h4: ["text-align"],
  h5: ["text-align"],
  h6: ["text-align"],
  span: ["font-family", "font-size"]
});

function preservedHtmlAttributes(element) {
  const tagName = element.tagName.toLowerCase();
  const managed = MANAGED_ATTRIBUTES_BY_TAG[tagName] || new Set();
  const attributes = {};

  for (const attribute of Array.from(element.attributes)) {
    const name = attribute.name.toLowerCase();
    if (!managed.has(name) && name !== "style") {
      attributes[name] = attribute.value;
    }
  }

  const originalStyle = element.getAttribute("style");
  if (originalStyle) {
    const styleElement = element.ownerDocument.createElement("span");
    styleElement.setAttribute("style", originalStyle);

    for (const property of MANAGED_STYLES_BY_TAG[tagName] || []) {
      styleElement.style.removeProperty(property);
    }

    const remainingStyle = styleElement.getAttribute("style");
    if (remainingStyle) attributes.style = remainingStyle;
  }

  return Object.keys(attributes).length ? attributes : null;
}

function tableRowsContentElement(tableElement) {
  const rows = tableElement.ownerDocument.createElement("tbody");

  function appendCleanRow(row) {
    const clone = row.cloneNode(true);

    // Defensive fallback for toolbar-created/editable tables. Indentation is
    // formatting, never another table cell.
    for (const child of Array.from(clone.childNodes)) {
      if (child.nodeType === 3 && !child.nodeValue.trim()) child.remove();
    }

    rows.appendChild(clone);
  }

  for (const child of Array.from(tableElement.children)) {
    const tagName = child.tagName.toLowerCase();

    if (tagName === "tr") {
      appendCleanRow(child);
      continue;
    }

    if (["thead", "tbody", "tfoot"].includes(tagName)) {
      for (const row of Array.from(child.children)) {
        if (row.tagName.toLowerCase() === "tr") {
          appendCleanRow(row);
        }
      }
    }
  }

  return rows;
}

// Tiptap 2.27's table serializer always generates a colgroup plus min-width,
// even when resizing is disabled. Keep only the structural tbody required by
// its schema so visual edits create the least surprising HTML possible.
const FidelityTable = Table.extend({
  addAttributes() {
    return {
      ...(this.parent?.() || {}),
      preservedHtmlAttributes: {
        default: null,
        parseHTML: preservedHtmlAttributes,
        renderHTML: (attributes) => attributes.preservedHtmlAttributes || {}
      }
    };
  },

  // TableView does additional structural parsing, so capture root attributes
  // explicitly instead of relying only on global-attribute rule injection.
  parseHTML() {
    return [{
      tag: "table",
      contentElement: tableRowsContentElement,
      getAttrs: (element) => ({
        preservedHtmlAttributes: preservedHtmlAttributes(element)
      })
    }];
  },

  renderHTML({ HTMLAttributes }) {
    return ["table", HTMLAttributes, ["tbody", 0]];
  }
});

const FidelityImage = Image.extend({
  addAttributes() {
    return {
      ...(this.parent?.() || {}),
      width: {
        default: null,
        parseHTML: (element) => element.getAttribute("width"),
        renderHTML: (attributes) => attributes.width ? { width: attributes.width } : {}
      },
      height: {
        default: null,
        parseHTML: (element) => element.getAttribute("height"),
        renderHTML: (attributes) => attributes.height ? { height: attributes.height } : {}
      }
    };
  }
});

const FidelityLink = Link.extend({
  addAttributes() {
    return {
      ...(this.parent?.() || {}),
      title: {
        default: null,
        parseHTML: (element) => element.getAttribute("title"),
        renderHTML: (attributes) => attributes.title ? { title: attributes.title } : {}
      }
    };
  }
});

/* ============================================================
 * PreserveAttributes — preserva atributos HTML não gerenciados
 * (class, style, id, data-*, aria-* e atributos de tabela) em
 * paragraph, heading, lists, blockquote, tabela, link e image. Sem isto,
 * Tiptap descarta tudo que não
 * está declarado no schema, removendo class="..." Bootstrap
 * etc. quando o usuário edita via "view source code".
 *
 * Implementado via TextAlign.extend() para reusar a base
 * Extension sem adicionar dependência em @tiptap/core
 * (mesmo padrão de FontFamily/FontSize acima).
 * ============================================================ */
const PreserveAttributes = TextAlign.extend({
  name: 'preserveAttributes',

  addOptions() {
    return {
      types: [
        'paragraph',
        'heading',
        'blockquote',
        'bulletList',
        'orderedList',
        'listItem',
        'codeBlock',
        'tableRow',
        'tableHeader',
        'tableCell',
        'image',
        'link',
        'taskList',
        'taskItem',
        'textStyle'
      ]
    };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          preservedHtmlAttributes: {
            default: null,
            parseHTML: preservedHtmlAttributes,
            renderHTML: (attributes) => {
              return attributes.preservedHtmlAttributes || {};
            }
          }
        }
      }
    ];
  },

  // Do not inherit TextAlign commands from the base class. The real TextAlign
  // extension above owns those commands; this extension only adds attributes.
  addCommands() { return {}; },
  addKeyboardShortcuts() { return {}; }
});

/* ============================================================
 * TextStyle — inline Mark definition.
 * Wraps content in <span style="..."> so that font-family and
 * font-size attributes have a Mark to attach to.
 * Not included in the vendor bundle; built via Underline.extend().
 * ============================================================ */
const TextStyle = Underline.extend({
  name: "textStyle",
  priority: 101,

  addOptions() {
    return { HTMLAttributes: {} };
  },

  parseHTML() {
    return [{ tag: "span" }];
  },

  renderHTML({ HTMLAttributes }) {
    return ["span", HTMLAttributes, 0];
  },

  addCommands() {
    return {
      removeEmptyTextStyle: () => ({ commands }) => commands.unsetMark(this.name)
    };
  },

  addKeyboardShortcuts() { return {}; },
  addInputRules()        { return []; },
  addPasteRules()        { return []; }
});

/* ============================================================
 * FontFamily — inline Extension definition.
 * Adds a fontFamily attribute to the textStyle mark and exposes
 * setFontFamily / unsetFontFamily commands.
 * Built via TextAlign.extend() to reuse the Extension base class.
 * ============================================================ */
const FontFamily = TextAlign.extend({
  name: "fontFamily",

  addOptions() {
    return { types: ["textStyle"] };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontFamily: {
            default: null,
            parseHTML: (el) => el.style.fontFamily?.replace(/['"]+/g, "") || null,
            renderHTML: (attrs) =>
              attrs.fontFamily ? { style: `font-family: ${attrs.fontFamily}` } : {}
          }
        }
      }
    ];
  },

  addCommands() {
    return {
      setFontFamily: (fontFamily) => ({ chain }) =>
        chain().setMark("textStyle", { fontFamily }).run(),
      unsetFontFamily: () => ({ chain }) =>
        chain().setMark("textStyle", { fontFamily: null }).run()
    };
  },

  addKeyboardShortcuts() { return {}; }
});

/* ============================================================
 * FontSize — inline Extension definition.
 * Adds a fontSize attribute to the textStyle mark and exposes
 * setFontSize / unsetFontSize commands.
 * Built via TextAlign.extend() to reuse the Extension base class.
 * ============================================================ */
const FontSize = TextAlign.extend({
  name: "fontSize",

  addOptions() {
    return { types: ["textStyle"] };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (el) => el.style.fontSize || null,
            renderHTML: (attrs) =>
              attrs.fontSize ? { style: `font-size: ${attrs.fontSize}` } : {}
          }
        }
      }
    ];
  },

  addCommands() {
    return {
      setFontSize: (fontSize) => ({ chain }) =>
        chain().setMark("textStyle", { fontSize }).run(),
      unsetFontSize: () => ({ chain }) =>
        chain().setMark("textStyle", { fontSize: null }).run()
    };
  },

  addKeyboardShortcuts() { return {}; }
});

export default {
  name: "BadiuTiptapEditor",

  components: {
    EditorContent,
    BadiuTiptapToolbar,
    BadiuTiptapImage,
    BadiuTiptapLink,
    BadiuTiptapSourceCode
  },

  props: {
    modelValue: {
      type: String,
      default: ""
    },

    placeholder: {
      type: String,
      default: "Type your content..."
    },

    showCounter: {
      type: Boolean,
      default: true
    },

    /** Altura fixa da área rolável. Aceita número (px) ou tamanho CSS. */
    editorHeight: {
      type: [String, Number],
      default: 320
    },

    /**
     * Preserva tabelas escritas no modo HTML byte a byte. No modo visual elas
     * são uma prévia atômica; um duplo clique reabre o código-fonte. Defina
     * false somente quando edição visual for mais importante que fidelidade.
     */
    preserveSourceTables: {
      type: Boolean,
      default: true
    },

    /** Nome anunciado por leitores de tela para a área contenteditable. */
    ariaLabel: {
      type: String,
      default: "Editor de conteúdo"
    },

    /**
     * Runtime configuration passada como objeto simples.
     *
     * Chaves suportadas:
     *   upload_baseurl       URL base do servidor Badiu para uploads
     *                        (ex.: "https://host/path/public").
     *   external_api_vendor  (opcional) Objeto vendor do host que expõe
     *                        Vue e axios (ex.: window.BADIUNET_SYSTEM_CORE_VENDOR).
     *                        Quando fornecido, axios é obtido dele; caso
     *                        contrário usa window.BADIUNET_SYSTEM_CORE_VENDOR
     *                        como fallback ou fica undefined (sem upload).
     *
     * Exemplo:
     *   :config="{ upload_baseurl: 'https://...', external_api_vendor: window.BADIUNET_SYSTEM_CORE_VENDOR }"
     */
    config: {
      type: Object,
      default: () => ({})
    }
  },

  emits: [
    "update:modelValue",
    "image-uploaded",
    "file-uploaded",
    "upload-error"
  ],

  setup(props, { emit }) {
    /* ============================================================
     * Base URL — getter / setter
     *
     * Priority order:
     *   1. Value set programmatically via o setter (_baseUrlOverride)
     *   2. config.upload_baseurl fornecido via prop
     *   3. Default embutido
     * ============================================================ */
    const _baseUrlOverride = ref(null);

    const baseUrl = computed({
      get() {
        return (
          _baseUrlOverride.value ??
          props.config?.upload_baseurl ??
          "https://ds4.badiu21.com.br/badiunet/geral/public"
        );
      },
      set(value) {
        _baseUrlOverride.value = value;
      }
    });

    /* ============================================================
     * Modal state (each modal is its own isolated component)
     * ============================================================ */
    const showImageModal  = ref(false);
    const showLinkModal   = ref(false);
    const showSourceModal = ref(false);

    const imageInitial = reactive({});
    const linkInitial  = reactive({});
    const sourceHtml   = ref("");

    /* ============================================================
     * Fullscreen state
     * ============================================================ */
    const fullscreen = ref(false);
    const editorStyle = computed(() => ({
      "--badiu-editor-content-height": toCssSize(props.editorHeight)
    }));

    // `htmlState` is the public/source representation. The ProseMirror document
    // below is only the editable visual representation and may be normalised by
    // its schema while parsing (for example, paragraphs inside table cells).
    const htmlState = createHtmlFidelityState(props.modelValue);
    const preservedHtmlRegistry = createPreservedHtmlRegistry();
    let isHydratingHtml = false;

    function toVisualHtml(html) {
      const source = typeof html === "string" ? html : "";
      return props.preserveSourceTables
        ? preservedHtmlRegistry.protectTables(source)
        : source;
    }

    /* ============================================================
     * Tiptap editor instance
     * ============================================================ */
    const extensions = [
      StarterKit,

      Underline,
      TextStyle,
      FontFamily,
      FontSize,

      FidelityImage.configure({
        inline: false,
        allowBase64: true
      }),

      FidelityLink.configure({
        openOnClick: false,
        autolink: false,
        linkOnPaste: false,
        HTMLAttributes: { rel: null, target: null }
      }),

      Placeholder.configure({
        placeholder: props.placeholder
      }),

      TextAlign.configure({
        types: ["heading", "paragraph"]
      }),

      createPreservedHtmlExtension(preservedHtmlRegistry, () => openSourceModal()),

      // Resizable tables inject colgroup/col/min-width metadata while merely
      // parsing source. Fidelity is more important than resize handles here.
      FidelityTable.configure({ resizable: false }),
      TableRow,
      TableHeader,
      TableCell,

      TaskList,

      TaskItem.configure({ nested: true }),

      CharacterCount,

      PreserveAttributes
    ].filter(Boolean);

    const editor = new Editor({
      content: toVisualHtml(props.modelValue),
      extensions,
      parseOptions: HTML_PARSE_OPTIONS,
      // Formatting is explicit through the toolbar. Markdown-like shortcuts
      // and paste rules must not silently create or rewrite HTML.
      enableInputRules: false,
      enablePasteRules: false,
      editorProps: {
        attributes: {
          role: "textbox",
          "aria-label": props.ariaLabel,
          "aria-multiline": "true"
        },

        handleDoubleClick(view, pos, event) {
          if (event.target && event.target.nodeName === "IMG") {
            openImageModal();
            return true;
          }
          return false;
        },

        /**
         * Handles image paste from the clipboard (screenshots, crops, etc.).
         * Images are inserted as base64 directly — no upload required.
         *
         * HTML images already present in the clipboard (with <img src>)
         * are handled natively by Tiptap via Image.configure({ allowBase64: true }).
         */
        handlePaste(view, event) {
          const clipboard = event.clipboardData;

          if (!clipboard || !clipboard.items) return false;

          for (let i = 0; i < clipboard.items.length; i++) {
            const item = clipboard.items[i];

            if (item.kind === "file" && item.type.startsWith("image/")) {
              const file = item.getAsFile();

              if (!file) continue;

              const reader = new FileReader();

              reader.onload = (e) => {
                const base64 = e.target.result;

                editor
                  .chain()
                  .focus()
                  .setImage({ src: base64 })
                  .run();
              };

              reader.readAsDataURL(file);

              event.preventDefault();
              return true; // paste handled — stop ProseMirror default
            }
          }

          return false; // let ProseMirror handle the paste normally
        }
      },
      onUpdate: ({ editor }) => {
        if (isHydratingHtml) return;

        const html = htmlState.acceptVisual(
          preservedHtmlRegistry.restore(editor.getHTML())
        );
        emit("update:modelValue", html);
      }
    });

    function hydrateEditorHtml(html) {
      isHydratingHtml = true;
      try {
        editor.commands.setContent(toVisualHtml(html), false, HTML_PARSE_OPTIONS);
      } finally {
        isHydratingHtml = false;
      }
    }

    watch(
      () => props.modelValue,
      (value) => {
        // A parent normally echoes our own v-model event. In source mode that
        // exact string intentionally differs from editor.getHTML(), so comparing
        // only with the Tiptap serializer would parse/reset the document twice.
        if (!editor) return;

        applyExternalHtml(htmlState, value, (html) => {
          hydrateEditorHtml(html);
        });
      }
    );

    watch(
      () => props.preserveSourceTables,
      () => hydrateEditorHtml(htmlState.value)
    );

    watch(
      () => props.ariaLabel,
      (value) => {
        editor.setOptions({
          editorProps: {
            ...editor.options.editorProps,
            attributes: {
              ...(editor.options.editorProps?.attributes || {}),
              "aria-label": value || "Editor de conteúdo"
            }
          }
        });
      }
    );

    function onDocumentKeydown(event) {
      if (
        event.key === "Escape" &&
        fullscreen.value &&
        !showImageModal.value &&
        !showLinkModal.value &&
        !showSourceModal.value
      ) {
        toggleFullscreen();
      }
    }

    onMounted(() => {
      document.addEventListener("keydown", onDocumentKeydown);
    });

    onBeforeUnmount(() => {
      document.removeEventListener("keydown", onDocumentKeydown);
      document.body.classList.remove("badiu-tiptap-fullscreen-lock");
      editor.destroy();
    });

    /* ============================================================
     * IMAGE
     * ============================================================ */
    function openImageModal() {
      const attrs = editor.getAttributes("image") || {};

      imageInitial.src    = attrs.src    || "";
      imageInitial.alt    = attrs.alt    || "";
      imageInitial.width  = attrs.width  || "";
      imageInitial.height = attrs.height || "";

      showImageModal.value = true;
    }

    function closeImageModal() {
      showImageModal.value = false;
    }

    function saveImage(payload) {
      editor.chain().focus().setImage(payload).run();
      closeImageModal();
    }

    function onImageUploaded(payload) {
      emit("image-uploaded", payload);
    }

    /* ============================================================
     * LINK
     * ============================================================ */
    function openLinkModal() {
      const attrs = editor.getAttributes("link") || {};

      const selection     = editor.state.selection;
      const selectedText  = editor.state.doc.textBetween(
        selection.from,
        selection.to,
        " "
      );

      linkInitial.href   = attrs.href   || "";
      linkInitial.target = attrs.target || "";
      linkInitial.title  = attrs.title  || "";
      linkInitial.text   = selectedText || "";

      showLinkModal.value = true;
    }

    function closeLinkModal() {
      showLinkModal.value = false;
    }

    function saveLink(payload) {
      const linkAttrs = { href: payload.href };

      if (payload.target) linkAttrs.target = payload.target;
      if (payload.title)  linkAttrs.title  = payload.title;

      const selection    = editor.state.selection;
      const hasSelection = selection.from !== selection.to;

      const originalSelectedText = hasSelection
        ? editor.state.doc.textBetween(selection.from, selection.to, " ")
        : "";

      const newText    = payload.text || payload.href;
      const textChanged = newText !== originalSelectedText;

      if (hasSelection && !textChanged) {
        // Text unchanged: only apply the link mark.
        editor
          .chain()
          .focus()
          .extendMarkRange("link")
          .setLink(linkAttrs)
          .run();
      } else if (hasSelection && textChanged) {
        // Text changed: replace the selection with the new text + link mark.
        editor
          .chain()
          .focus()
          .extendMarkRange("link")
          .deleteSelection()
          .insertContent({
            type: "text",
            text: newText,
            marks: [{ type: "link", attrs: linkAttrs }]
          })
          .run();
      } else {
        // No selection: insert new text with the link mark applied.
        editor
          .chain()
          .focus()
          .insertContent({
            type: "text",
            text: newText,
            marks: [{ type: "link", attrs: linkAttrs }]
          })
          .run();
      }

      closeLinkModal();
    }

    function removeLink() {
      editor
        .chain()
        .focus()
        .extendMarkRange("link")
        .unsetLink()
        .run();

      closeLinkModal();
    }

    function onLinkUploaded(payload) {
      emit("file-uploaded", payload);
    }

    /* ============================================================
     * SOURCE CODE
     * ============================================================ */
    function openSourceModal() {
      sourceHtml.value       = htmlState.value;
      showSourceModal.value  = true;
    }

    function closeSourceModal() {
      showSourceModal.value = false;
    }

    function saveSource(html) {
      // Parse without emitting Tiptap's canonical serializer. The exact source
      // remains the v-model value until a real visual editing transaction occurs.
      commitSourceHtml(htmlState, html, {
        render(source) {
          hydrateEditorHtml(source);
        },
        emit(source) {
          emit("update:modelValue", source);
        }
      });
      closeSourceModal();
    }

    /* ============================================================
     * TABLE
     * ============================================================ */
    function insertTable() {
      editor
        .chain()
        .focus()
        .insertTable({
          rows: 3,
          cols: 3,
          withHeaderRow: true
        })
        .run();
    }

    /* ============================================================
     * FULLSCREEN
     * ============================================================ */
    function toggleFullscreen() {
      fullscreen.value = !fullscreen.value;

      if (fullscreen.value) {
        document.body.classList.add("badiu-tiptap-fullscreen-lock");
      } else {
        document.body.classList.remove("badiu-tiptap-fullscreen-lock");
      }
    }

    /* ============================================================
     * PASTE — base64 images
     * ============================================================
     *
     * The handler lives in editorProps.handlePaste (see editor
     * instantiation above). That is the idiomatic Tiptap approach:
     * it goes through the ProseMirror pipeline instead of relying
     * on DOM events bubbling through the template.
     */

    /* ============================================================
     * ERRORS
     * ============================================================ */
    function onUploadError(payload) {
      emit("upload-error", payload);
    }

    return {
      // Editor
      editor,

      // Configuration
      baseUrl,

      // Modals
      showImageModal,
      showLinkModal,
      showSourceModal,
      imageInitial,
      linkInitial,
      sourceHtml,

      // UI state
      fullscreen,
      editorStyle,

      // Handlers
      openImageModal,
      closeImageModal,
      saveImage,
      onImageUploaded,

      openLinkModal,
      closeLinkModal,
      saveLink,
      removeLink,
      onLinkUploaded,

      openSourceModal,
      closeSourceModal,
      saveSource,

      insertTable,
      toggleFullscreen,

      onUploadError
    };
  }
};
</script>

<style>
/* ============================================================
 * Editor container — escopado para resistir a CSS do host (ex.:
 * Bootstrap dentro de um .row/.col que poderia herdar flex,
 * margens negativas, larguras etc.).
 * ============================================================ */
.badiu-tiptap-editor {
  position: relative;
  display: block;
  width: 100%;
  box-sizing: border-box;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  overflow: hidden;
  background: #fff;
  /* Reset de flex/grid herdado do host. */
  flex: none;
  margin: 0;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.badiu-tiptap-editor:focus-within {
  border-color: #86b7fe;
  box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.16);
}

.badiu-tiptap-editor *,
.badiu-tiptap-editor *::before,
.badiu-tiptap-editor *::after {
  box-sizing: border-box;
}

/* ============================================================
 * Editable content area. Only this viewport scrolls in normal mode, keeping
 * toolbar and status visible even for very long documents.
 * ============================================================ */
.badiu-tiptap-editor .badiu-tiptap-content {
  display: block;
  padding: 14px 16px;
  height: var(--badiu-editor-content-height, 320px);
  min-height: 0;
  overflow: auto;
  overscroll-behavior: contain;
  scrollbar-gutter: stable;
  background: #fff;
}

.badiu-tiptap-editor .badiu-tiptap-content .ProseMirror {
  outline: none;
  min-height: 100%;
  line-height: 1.5;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  word-break: normal;
}

/* Deliberate document rhythm prevents browser/Bootstrap paragraph margins
 * from being added to the editor's own spacing. Inline styles still win. */
.badiu-tiptap-editor .badiu-tiptap-content .ProseMirror p {
  margin: 0;
}

.badiu-tiptap-editor .badiu-tiptap-content .ProseMirror p + p {
  margin-top: 0.375em;
}

.badiu-tiptap-editor .badiu-tiptap-content .ProseMirror h1,
.badiu-tiptap-editor .badiu-tiptap-content .ProseMirror h2,
.badiu-tiptap-editor .badiu-tiptap-content .ProseMirror h3,
.badiu-tiptap-editor .badiu-tiptap-content .ProseMirror h4,
.badiu-tiptap-editor .badiu-tiptap-content .ProseMirror h5,
.badiu-tiptap-editor .badiu-tiptap-content .ProseMirror h6 {
  line-height: 1.25;
  margin: 0.9em 0 0.35em;
}

.badiu-tiptap-editor .badiu-tiptap-content .ProseMirror > :first-child {
  margin-top: 0;
}

.badiu-tiptap-editor .badiu-tiptap-content .ProseMirror ul,
.badiu-tiptap-editor .badiu-tiptap-content .ProseMirror ol {
  margin: 0.5em 0;
  padding-left: 1.5rem;
}

.badiu-tiptap-editor .badiu-tiptap-content .ProseMirror li {
  margin: 0.125em 0;
}

.badiu-tiptap-editor .badiu-tiptap-content .ProseMirror li > p {
  margin: 0;
}

.badiu-tiptap-editor .badiu-tiptap-content .ProseMirror blockquote {
  margin: 0.75em 0;
  padding-left: 0.875rem;
  color: #495057;
  border-left: 3px solid #ced4da;
}

.badiu-tiptap-editor .badiu-tiptap-content .ProseMirror pre {
  margin: 0.75em 0;
  padding: 0.75rem;
  overflow: auto;
  white-space: pre-wrap;
  background: #f8f9fa;
  border-radius: 0.25rem;
}

.badiu-tiptap-editor .badiu-tiptap-content .ProseMirror hr {
  margin: 1em 0;
}

.badiu-tiptap-editor .badiu-tiptap-content .ProseMirror p.is-editor-empty:first-child::before {
  content: attr(data-placeholder);
  float: left;
  color: #adb5bd;
  pointer-events: none;
  height: 0;
}

/* ============================================================
 * Image node
 * ============================================================ */
.badiu-tiptap-editor .badiu-tiptap-content .ProseMirror img {
  max-width: 100%;
  height: auto;
  cursor: default;
}

.badiu-tiptap-editor .badiu-tiptap-content .ProseMirror img.ProseMirror-selectednode {
  outline: 3px solid #0d6efd;
  outline-offset: 2px;
}

/* ============================================================
 * Table
 * ============================================================ */
.badiu-tiptap-editor .badiu-preserved-html-block {
  position: relative;
  margin: 0.75em 0;
  overflow-x: auto;
  contain: layout paint;
  isolation: isolate;
  border: 1px solid transparent;
  border-radius: 0.375rem;
}

.badiu-tiptap-editor .badiu-preserved-html-block:hover,
.badiu-tiptap-editor .badiu-preserved-html-block:focus-visible,
.badiu-tiptap-editor .badiu-preserved-html-block.ProseMirror-selectednode {
  border-color: #86b7fe;
  outline: none;
  box-shadow: 0 0 0 0.15rem rgba(13, 110, 253, 0.12);
}

.badiu-tiptap-editor .badiu-preserved-html-controls {
  display: flex;
  justify-content: flex-end;
  padding: 0.25rem;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

.badiu-tiptap-editor .badiu-preserved-html-edit {
  appearance: none;
  padding: 0.2rem 0.55rem;
  color: #0d6efd;
  font: inherit;
  font-size: 0.75rem;
  line-height: 1.35;
  cursor: pointer;
  background: #fff;
  border: 1px solid #86b7fe;
  border-radius: 0.25rem;
}

.badiu-tiptap-editor .badiu-preserved-html-edit:hover,
.badiu-tiptap-editor .badiu-preserved-html-edit:focus-visible {
  color: #fff;
  background: #0d6efd;
  outline: none;
}

.badiu-tiptap-editor .badiu-preserved-html-unavailable {
  padding: 0.75rem;
  color: #6c757d;
}

.badiu-tiptap-editor .badiu-tiptap-content .ProseMirror table {
  border-collapse: collapse;
  margin: 0.75em 0;
  table-layout: fixed;
  width: 100%;
}

.badiu-tiptap-editor .badiu-preserved-html-block > table {
  margin: 0;
}

.badiu-tiptap-editor .badiu-tiptap-content .ProseMirror th,
.badiu-tiptap-editor .badiu-tiptap-content .ProseMirror td {
  border: 1px solid #dee2e6;
  padding: 6px 10px;
  vertical-align: top;
  min-width: 40px;
}

.badiu-tiptap-editor .badiu-tiptap-content .ProseMirror th {
  background: #f8f9fa;
  font-weight: 600;
}

/* ============================================================
 * Fullscreen
 * ============================================================ */
.badiu-tiptap-editor.badiu-tiptap-fullscreen {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  inset: 0;
  width: 100vw !important;
  height: 100vh !important;
  height: 100dvh !important;
  max-width: none !important;
  max-height: none !important;
  z-index: 99999;
  display: flex !important;
  flex-direction: column !important;
  background: #fff;
  border-radius: 0 !important;
  margin: 0 !important;
}

.badiu-tiptap-editor.badiu-tiptap-fullscreen .badiu-tiptap-content {
  flex: 1 1 auto;
  height: auto;
  overflow: auto;
  min-height: 0;
}

body.badiu-tiptap-fullscreen-lock {
  overflow: hidden;
}

/* ============================================================
 * Status bar (footer)
 * ============================================================ */
.badiu-tiptap-editor .badiu-tiptap-statusbar {
  display: flex !important;
  justify-content: space-between !important;
  align-items: center !important;
  padding: 0.25rem 0.75rem !important;
  background: #f8f9fa !important;
  border-top: 1px solid #dee2e6 !important;
  font-size: 0.8rem;
  color: #6c757d;
}

.badiu-tiptap-editor .badiu-tiptap-counter {
  font-variant-numeric: tabular-nums;
  color: #6c757d;
}

.badiu-tiptap-editor .badiu-tiptap-brand {
  margin-left: auto;
  font-style: italic;
  color: #6c757d;
}
</style>
